/**
 * NotificationItem - Sprint 19b
 *
 * Single notification item in the drawer.
 * @see US-19b.07
 */

import {
  Package,
  ClipboardList,
  Truck,
  ShoppingCart,
  AlertTriangle,
  FileText,
} from 'lucide-react';

import type { Notification, NotificationEntityType } from '@bstock/shared';
import { useFormatDate } from '@/hooks';

/**
 * Format relative time in French with timezone-aware fallback
 */
function useFormatRelativeTime() {
  const { formatDateShort } = useFormatDate();

  return (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return 'a l\'instant';
    if (diffMin < 60) return `il y a ${diffMin} min`;
    if (diffHour < 24) return `il y a ${diffHour} h`;
    if (diffDay === 1) return 'hier';
    if (diffDay < 7) return `il y a ${diffDay} j`;
    return formatDateShort(dateString);
  };
}

interface NotificationItemProps {
  notification: Notification;
  onClick: (notification: Notification) => void;
}

/**
 * Get icon for notification entity type
 */
function getIcon(entityType: NotificationEntityType | null) {
  switch (entityType) {
    case 'TRANSFER':
      return Truck;
    case 'INVENTORY':
      return ClipboardList;
    case 'GOODS_RECEIPT':
      return Package;
    case 'PURCHASE_ORDER':
      return ShoppingCart;
    case 'LOSS':
      return AlertTriangle;
    default:
      return FileText;
  }
}

export function NotificationItem({ notification, onClick }: NotificationItemProps) {
  const Icon = getIcon(notification.entityType);
  const formatRelativeTime = useFormatRelativeTime();
  const timeAgo = formatRelativeTime(notification.createdAt);

  return (
    <button
      type="button"
      onClick={() => onClick(notification)}
      className={`
        w-full text-left
        flex items-start gap-3
        p-3 rounded-lg
        transition-colors
        ${notification.isRead
          ? 'bg-white hover:bg-neutral-50'
          : 'bg-primary-50 hover:bg-primary-100'
        }
      `}
    >
      {/* Icon */}
      <div
        className={`
          flex-shrink-0
          w-9 h-9 rounded-full
          flex items-center justify-center
          ${notification.isRead
            ? 'bg-neutral-100 text-neutral-500'
            : 'bg-primary-100 text-primary-600'
          }
        `}
      >
        <Icon className="w-4 h-4" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p
            className={`
              text-sm font-medium truncate
              ${notification.isRead ? 'text-neutral-700' : 'text-neutral-900'}
            `}
          >
            {notification.title}
          </p>
          {!notification.isRead && (
            <span className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-primary-500" />
          )}
        </div>
        <p className="text-sm text-neutral-500 line-clamp-2">
          {notification.message}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-neutral-400">{timeAgo}</span>
          {notification.actorName && (
            <>
              <span className="text-xs text-neutral-300">-</span>
              <span className="text-xs text-neutral-400 truncate">
                {notification.actorName}
              </span>
            </>
          )}
        </div>
      </div>
    </button>
  );
}
